import {
  Body1,
  Button,
  Divider,
  Subtitle1,
  Subtitle2,
  Title2,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { OpenRegular } from '@fluentui/react-icons';

import {
  AreaChart,
  ClientObservedSignalSummary,
  EmptyState,
  ErrorState,
  FreshnessChip,
  KpiTile,
  LoadingState,
  SectionCard,
  StatusPill,
  UptimeHeatmap,
  uptimePctToString,
  type StatusKind,
} from '@/design-system';
import type { ResourceHealth } from '@/api/types';
import type { ResourceDetail, ResourceMetricSnapshot } from '@/models/resourceDetail';
import { spacing, statusPalette } from '@/theme/tokens';

interface ResourceDetailPanelProps {
  detail: ResourceDetail | undefined;
  loading: boolean;
  isRefreshing: boolean;
  error: Error | null;
  onRetry: () => void;
}

const useStyles = makeStyles({
  panel: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.xl,
    padding: spacing.xl,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusLarge,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    minHeight: '480px',
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: spacing.l,
  },
  headerLeft: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.xxs,
    flex: 1,
    minWidth: 0,
  },
  meta: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.s,
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    flexWrap: 'wrap',
  },
  metaDot: {
    width: '4px',
    height: '4px',
    borderRadius: '50%',
    backgroundColor: tokens.colorNeutralForeground4,
  },
  uptimeHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: spacing.m,
  },
  kpiGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
    gap: spacing.m,
  },
  chartGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))',
    gap: spacing.l,
  },
});

const STATUS_TO_KIND: Record<ResourceHealth['status'], StatusKind> = {
  Healthy: 'healthy',
  Degraded: 'degraded',
  Unhealthy: 'unhealthy',
  Unknown: 'unknown',
};

/**
 * Human-readable criticality label. Lower-cased contract values read awkwardly
 * inline ("Criticality: critical"); display the contract value as a noun
 * phrase instead.
 */
const CRITICALITY_LABEL: Record<ResourceHealth['criticality'], string> = {
  Critical: 'Critical resource',
  Important: 'Important resource',
  Informational: 'Informational',
};

const HEALTHY_THRESHOLD = 99.9;
const DEGRADED_THRESHOLD = 99.0;
const PERCENT = 100;

function uptimeTone(pct: number): 'healthy' | 'degraded' | 'unhealthy' {
  if (pct >= HEALTHY_THRESHOLD) {
    return 'healthy';
  }
  if (pct >= DEGRADED_THRESHOLD) {
    return 'degraded';
  }
  return 'unhealthy';
}

export function ResourceDetailPanel({
  detail,
  loading,
  isRefreshing,
  error,
  onRetry,
}: ResourceDetailPanelProps) {
  const classes = useStyles();

  if (loading && !detail) {
    return (
      <div className={classes.panel}>
        <LoadingState label="Loading resource detail…" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={classes.panel}>
        <ErrorState
          title="Couldn't load this resource"
          description={error.message}
          retry={onRetry}
        />
      </div>
    );
  }

  if (!detail) {
    return (
      <div className={classes.panel}>
        <EmptyState
          title="Pick a resource"
          description="Choose a resource on the left to see its uptime, latency, and provider-specific metrics."
        />
      </div>
    );
  }

  const { health, metrics, portalDeepLink } = detail;
  const tone = uptimeTone(health.uptimePct);

  return (
    <div className={classes.panel}>
      <header className={classes.header}>
        <div className={classes.headerLeft}>
          <Title2 as="h2">{health.name}</Title2>
          <div className={classes.meta}>
            <span>{health.kind}</span>
            <span className={classes.metaDot} aria-hidden="true" />
            <span>{health.region}</span>
            <span className={classes.metaDot} aria-hidden="true" />
            <span>{CRITICALITY_LABEL[health.criticality]}</span>
          </div>
          <FreshnessChip asOfMs={health.observedAt} isRefreshing={isRefreshing} />
        </div>
        <StatusPill kind={STATUS_TO_KIND[health.status]} />
      </header>

      <SectionCard title="Uptime — last 30 days">
        <div className={classes.uptimeHeader}>
          <Subtitle1
            style={{
              color:
                tone === 'healthy'
                  ? statusPalette.healthy
                  : tone === 'degraded'
                    ? statusPalette.degraded
                    : statusPalette.unhealthy,
            }}
          >
            {uptimePctToString(health.uptimePct / PERCENT)}
          </Subtitle1>
          {portalDeepLink ? (
            <Button
              as="a"
              href={portalDeepLink}
              target="_blank"
              rel="noopener noreferrer"
              icon={<OpenRegular />}
              size="small"
              appearance="subtle"
            >
              Open in Azure portal
            </Button>
          ) : null}
        </div>
        <UptimeHeatmap
          data={health.last30Days.map((d) => ({
            dayStartEpochMs: d.dayStartEpochMs,
            uptimePct: d.uptimePct,
          }))}
        />
      </SectionCard>

      <SectionCard title="Client-observed dependency health">
        <ClientObservedSignalSummary label="Dependencies" signals={detail.dependencySignals ?? []} />
      </SectionCard>

      <Divider />

      {metrics.length > 0 ? (
        <>
          <Subtitle2 as="h3">Metrics</Subtitle2>
          <KpiRow metrics={metrics} />
          <div className={classes.chartGrid}>
            {metrics.map((m) => (
              <SectionCard key={m.key} title={m.label}>
                <AreaChart
                  series={[m.series]}
                  unit={m.unit}
                  tone={m.tone}
                  height={180}
                  hideLegend
                />
              </SectionCard>
            ))}
          </div>
        </>
      ) : (
        <Body1>No metrics configured for this resource yet.</Body1>
      )}
    </div>
  );
}

function KpiRow({ metrics }: { metrics: readonly ResourceMetricSnapshot[] }) {
  const classes = useStyles();
  return (
    <div className={classes.kpiGrid}>
      {metrics.map((m) => {
        const points = m.series.points;
        // points may be empty at runtime even though TS — without
        // noUncheckedIndexedAccess — types each access as defined; guard
        // explicitly so we don't render NaN headlines.
        const last = points.length > 0 ? points[points.length - 1].v : undefined;
        const first = points.length > 0 ? points[0].v : undefined;
        const headline = m.headlineValue ?? last ?? 0;
        const previous = m.previousHeadlineValue ?? first;
        return (
          <KpiTile
            key={m.key}
            label={m.label}
            value={headline}
            previousValue={previous}
            unit={m.unit}
            tone={m.tone}
            lowerIsBetter={m.lowerIsBetter}
            caption={previous === undefined ? undefined : 'vs. window start'}
          />
        );
      })}
    </div>
  );
}
